import { RefObject, useCallback, MouseEvent as ReactMouseEvent } from 'react'

export const useOnHorizontalThumbMouseDown = (
    contentRef: RefObject<HTMLDivElement>,
    setScrollStartPosition: (value: number) => void,
    setInitialScrollLeft: (value: number) => void,
    setIsThumbDragging: (value: boolean) => void
) => {
    return useCallback(
        (event: ReactMouseEvent) => {
            event.preventDefault()
            event.stopPropagation()
            setScrollStartPosition(event.clientX)

            if (contentRef.current) {
                setInitialScrollLeft(contentRef.current.scrollLeft)
            }
            setIsThumbDragging(true)
        },
        [
            contentRef.current,
            setScrollStartPosition,
            setInitialScrollLeft,
            setIsThumbDragging,
        ]
    )
}

export const useOnHorizontalThumbMouseUp = (
    isThumbDragging: boolean,
    setIsThumbDragging: (value: boolean) => void
) => {
    return useCallback(
        (event: ReactMouseEvent) => {
            event.preventDefault()
            event.stopPropagation()

            if (isThumbDragging) {
                setIsThumbDragging(false)
            }
        },
        [isThumbDragging, setIsThumbDragging]
    )
}

/**
 * Scroll content horizontally when scroll track is clicked
 * @param scrollTrackRef
 * @param contentRef
 * @param thumbWidth
 */
export const useOnHorizontalTrackClick = (
    scrollTrackRef: RefObject<HTMLDivElement>,
    contentRef: RefObject<HTMLDivElement>,
    thumbWidth: number
) => {
    return useCallback(
        (event: ReactMouseEvent) => {
            event.preventDefault()
            event.stopPropagation()

            const { current: track } = scrollTrackRef
            const { current: content } = contentRef

            if (track && content) {
                const { clientX } = event
                const target = event.target as HTMLDivElement
                const rect = target.getBoundingClientRect()
                const trackLeft = rect.left
                const thumbOffset = -(thumbWidth / 2)
                const clickRatio =
                    (clientX - trackLeft + thumbOffset) / track.clientWidth
                const scrollAmount = Math.floor(
                    clickRatio * content.scrollWidth
                )
                content.scrollTo({
                    left: scrollAmount,
                    behavior: 'smooth',
                })
            }
        },
        [scrollTrackRef.current, contentRef.current, thumbWidth]
    )
}

/**
 * Move horizontal scroll thumb when dragging
 * @param contentRef
 * @param isDragging
 * @param startPosition
 * @param initialContentScrollLeft
 * @param thumbWidth
 */
export const useOnHorizontalThumbMove = (
    contentRef: RefObject<HTMLDivElement>,
    isDragging: boolean,
    startPosition: number,
    initialContentScrollLeft: number,
    thumbWidth: number
) => {
    return useCallback(
        (event: ReactMouseEvent) => {
            //
            if (contentRef.current) {
                event.preventDefault()
                event.stopPropagation()
                if (isDragging) {
                    const {
                        scrollWidth: contentScrollWidth,
                        clientWidth: contentClientWidth,
                    } = contentRef.current

                    const deltaX =
                        (event.clientX - startPosition) *
                        (contentClientWidth / thumbWidth)

                    contentRef.current.scrollLeft = Math.min(
                        initialContentScrollLeft + deltaX,
                        contentScrollWidth - contentClientWidth
                    )
                }
            }
        },
        [
            contentRef.current,
            isDragging,
            startPosition,
            initialContentScrollLeft,
            thumbWidth,
        ]
    )
}

/**
 * Move scroll thumb horizontally when scrolling mouse wheel
 */
export const useOnHorizontalThumbScroll = (
    contentRef: RefObject<HTMLDivElement>,
    scrollTrackRef: RefObject<HTMLDivElement>,
    scrollThumbRef: RefObject<HTMLDivElement>,
    thumbWidth: number
) => {
    return useCallback(
        (event: Event) => {
            if (
                !contentRef.current ||
                !scrollTrackRef.current ||
                !scrollThumbRef.current
            ) {
                return
            }

            const { scrollLeft: contentLeft, scrollWidth: contentWidth } =
                contentRef.current
            const { clientWidth: trackWidth } = scrollTrackRef.current

            let newLeft = (contentLeft / contentWidth) * trackWidth
            newLeft = Math.min(newLeft, trackWidth - thumbWidth)
            const thumb = scrollThumbRef.current
            requestAnimationFrame(() => {
                thumb.style.left = `${newLeft}px`
            })
        },
        [
            contentRef.current,
            scrollTrackRef.current,
            scrollThumbRef.current,
            // thumbWidth,
        ]
    )
}
